import { Subscription } from 'rxjs';

import { Component, OnDestroy, OnInit } from '@angular/core';

import { EwidencjaService } from '../../../@core/http/ewidencja.service';
import { SumowanieKwotService } from '../../../@core/utils/sumowanie-kwot.service';
import { Kpir } from '../../../shared/models/kpir.model';
import { ZakresDat } from '../../../shared/models/zakres-dat.model';
import { KomunikatyTabeli, KomunikatyTabeliService } from '../../../@core/utils/komunikaty-tabeli.service';
import { TranslateService } from '@ngx-translate/core';
import { WydrukcService } from '../../../@core/http/wydruk.service';
import { formatDate } from '@angular/common';

@Component({
  selector: 'ngx-kpir',
  templateUrl: './kpir.component.html',
  styleUrls: ['./kpir.component.scss'],
})
export class KpirComponent implements OnInit, OnDestroy {

  dane: Kpir[];
  temp = [];
  eksportKolumny = [];
  eksportPdfKolumnyStyle;
  zakresDat: ZakresDat;
  private ewidencjeSubscription: Subscription;
  wyswietlanieFiltrow = false;

  komunikatyTabeli: KomunikatyTabeli;

  constructor(
    private readonly ewidencjeService: EwidencjaService,
    private readonly komunikatyTabeliService: KomunikatyTabeliService,
    private readonly translate: TranslateService,
    private readonly wydrukService: WydrukcService,
    ) { }

  ngOnInit() {
    this.odswiezKomunikatyTabeli();
    this.translate.onLangChange.subscribe(() => this.odswiezKomunikatyTabeli());

    this.odswiezKolumnyPdf();
    this.translate.onLangChange.subscribe(() => this.odswiezKolumnyPdf());
  }

  ngOnDestroy(): void {
    this.ewidencjeSubscription.unsubscribe();
  }

  updateFilter(event, prop) {
    const val = event.target.value.toLowerCase();
    const temp = this.temp.filter(function(d) {
      return (d[prop] != null && d[prop].toString().toLowerCase().indexOf(val) !== -1) || !val;
    });
    this.dane = temp;
  }

  onChangeZmianaZakresu(zakresDat: ZakresDat) {
    this.zakresDat = zakresDat;
    this.ewidencjeSubscription = this.ewidencjeService
      .getKpir(zakresDat)
      .subscribe(response => {
        this.temp = [...response];
        this.dane = response;
      });
  }

  sumaKwota(value: number[]) {
    return new SumowanieKwotService().suma(value);
  }

  exportPdf() {
      this.wydrukService.drukujKPiR(this.zakresDat).subscribe(data => {
        const blob = new Blob([data], {type: 'application/pdf'});
        saveAs(blob, 'kpir_' + formatDate(new Date(), 'yyyyMMdd_HHmmss', 'pl-PL') +  '.pdf');
      });
  }

  updateFilterMobile(event) {
    const val = event.target.value.toLowerCase();
    const temp = this.temp.filter(function(d) {
      return (d.numerDowoduKsiegowego != null && d.numerDowoduKsiegowego.toString().toLowerCase().indexOf(val) !== -1) || 
             (d.kontrahentNazwa != null && d.kontrahentNazwa.toString().toLowerCase().indexOf(val) !== -1) ||
             (d.data != null && d.data.toString().toLowerCase().indexOf(val) !== -1) ||
             (d.opisZdarzeniaGospodarczego != null && d.opisZdarzeniaGospodarczego.toString().toLowerCase().indexOf(val) !== -1) ||
             !val;
    });
    this.dane = temp;
  }

  sumaPrzychodTowaryUslugi(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.przychodTowaryUslugi || 0), 0);
  }

  sumaPrzychodPozostale(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.przychodPozostale || 0), 0);
  }

  sumaPrzychodRazem(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.przychodRazem || 0), 0);
  }

  sumaZakupTowary(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.zakupTowary || 0), 0);
  }

  sumaZakupKosztyUboczne(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.zakupKosztyUboczne || 0), 0);
  }

  sumaWydatkiWynagrodzenie(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.wydatkiWynagrodzenie || 0), 0);
  }

  sumaWydatkiPozostale(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.wydatkiPozostale || 0), 0);
  }

  sumaWydatkiRazem(): number {
    if (!this.dane) return 0;
    return this.dane.reduce((sum, item) => sum + (item.wydatkiRazem || 0), 0);
  }

  private odswiezKomunikatyTabeli() {
    this.komunikatyTabeli = this.komunikatyTabeliService.get();
  }

  private odswiezKolumnyPdf() {
    this.eksportKolumny = [
      {header: this.translate.instant('etykieta.lp'), dataKey: 'lp'},
      {header: this.translate.instant('etykieta.data'), dataKey: 'data'},
      {header: this.translate.instant('etykieta.nr-dowodu-ksiegowego'), dataKey: 'numerDowoduKsiegowego'},
      {header: this.translate.instant('etykieta.kontrahent-nazwa'), dataKey: 'kontrahentNazwa'},
      {header: this.translate.instant('etykieta.opis-zdarzenia-gospodarczego'), dataKey: 'opisZdarzeniaGospodarczego'},
      {header: this.translate.instant('etykieta.przychod-wartosc-sprzedazy-towarow'), dataKey: 'przychodTowaryUslugi'},
      {header: this.translate.instant('etykieta.przychod-pozostale'), dataKey: 'przychodPozostale'},
      {header: this.translate.instant('etykieta.przychod-razem'), dataKey: 'przychodRazem'},
      {header: this.translate.instant('etykieta.zakup-towary'), dataKey: 'zakupTowary'},
      {header: this.translate.instant('etykieta.zakup-koszty-uboczne'), dataKey: 'zakupKosztyUboczne'},
      {header: this.translate.instant('etykieta.wydatki-wynagrodzenie'), dataKey: 'wydatkiWynagrodzenie'},
      {header: this.translate.instant('etykieta.wydatki-pozostale'), dataKey: 'wydatkiPozostale'},
      {header: this.translate.instant('etykieta.wydatki-razem'), dataKey: 'wydatkiRazem'},
    ];

    this.eksportPdfKolumnyStyle = {
      przychodTowaryUslugi: { halign: 'right' },
      przychodPozostale: { halign: 'right' },
      przychodRazem: { halign: 'right' },
      zakupTowary: { halign: 'right' },
      zakupKosztyUboczne: { halign: 'right' },
      wydatkiWynagrodzenie: { halign: 'right' },
      wydatkiPozostale: { halign: 'right' },
      wydatkiRazem: { halign: 'right' },
    };
  }
}
